import config from 'config';
import debug from 'debug';
import { batch } from 'react-redux';
import { push } from 'connected-react-router';
import {
  ANGAssetAdapter as GenericANGAssetAdapter,
  ANGAssetHighlightAdapter as GenericANGAssetHighlightAdapter,
  getType
} from '@mulesoft/exchange-ui-portals-common/lib/types/adapters';
import searchDefinitions from '@mulesoft/exchange-assets-definitions/lib/search';
import {
  ANG_SIDEBAR_CONSISTENCY_ENABLED,
  ANG_DEFAULT_ENABLED
} from '~/domains/featureFlags/keys';
import {
  actions as sessionActions,
  selectors as sessionSelectors
} from '~/portals/store/domains/session';
import { selectors as commonSelectors } from '~/domains/common';
import { selectors as featureFlagsSelectors } from '~/domains/featureFlags';
import * as searchService from '~/services/searchService';
import ANGFactory from '~/utils/ang/ANGFactory';
import ANGFilters from '~/utils/ang/ANGFilters';
import { shouldGetFacetsWithOrganizationId } from '~/utils/ang/query';
import * as angConstants from '~/utils/ang/constants';
import { getBelongingOrganizationIds } from '~/utils/organization';
import { actions as analyticsActions } from '~/domains/analytics';
import { EVENT_TYPES } from '~/analytics/events';
import { actions as modalActions } from '~/domains/modal';
import ANGOptOutModal from '~/components/Modals/ANGOptOutModal';
import ANGOnboardingModal from '~/components/Modals/ANGOnboardingModal';
import * as actions from './actionTypes';
import * as selectors from './selectors';

const log = debug('exchange:ang-search:actions');

export function addAsset(
  { asset, highlight },
  batchNumber,
  isANGPaginationEnabled
) {
  return {
    type: actions.ADD_LIST_ASSET,
    asset,
    batchNumber,
    highlight,
    ...(isANGPaginationEnabled ? { isANGPaginationEnabled } : {})
  };
}

export function triggerSearch(limit) {
  return {
    type: actions.TRIGGER_SEARCH,
    ...(limit ? { limit } : {})
  };
}

export function finishSearch() {
  return {
    type: actions.FINISH_SEARCH
  };
}

export function setFacets(facets) {
  return {
    type: actions.SET_FACETS,
    facets
  };
}

export function setRootOrganizations(rootOrganizations) {
  return {
    type: actions.SET_ROOT_ORGANIZATIONS,
    rootOrganizations
  };
}

export function handleFacetsError() {
  return {
    type: actions.FETCHING_FACETS_ERROR
  };
}

export function updateFacets(facets) {
  return {
    type: actions.UPDATE_FACETS,
    facets
  };
}

export function getFacets(query, assetTypesConfiguration, rootOrganizations) {
  return async (dispatch, getState) => {
    const state = getState();
    const shouldRefreshFacets = selectors.shouldRefreshFacets(state, query);

    if (!shouldRefreshFacets) {
      return;
    }

    const isANGSidebarConsistencyEnabled = featureFlagsSelectors.featureFlag(
      state,
      ANG_SIDEBAR_CONSISTENCY_ENABLED
    );
    const context = sessionSelectors.context(state);
    const profile = sessionSelectors.profile(state);
    const organizationId = profile && profile.organization.id;
    const filters = ANGFilters.buildFromQuery(
      query,
      assetTypesConfiguration,
      profile,
      isANGSidebarConsistencyEnabled,
      rootOrganizations
    );
    const parsedQuery = filters.getAsQuery();
    const shouldIncludeOrganizationId = shouldGetFacetsWithOrganizationId(
      query,
      isANGSidebarConsistencyEnabled
    );
    const start = Date.now();

    try {
      const facets = await searchService.getFacets({
        context,
        query: parsedQuery,
        assetTypesConfiguration,
        ...(shouldIncludeOrganizationId ? { organizationId } : {})
      });
      const responseTime = Date.now() - start;

      dispatch(setFacets({ ...facets, responseTime }));
    } catch (error) {
      if (error.status === 401) {
        throw error;
      }

      dispatch(handleFacetsError());
    }
  };
}

export function getRootOrganizations() {
  return async (dispatch, getState) => {
    const state = getState();
    const profile = commonSelectors.profile(state);
    const context = sessionSelectors.context(state);

    if (!profile) {
      return null;
    }

    let facets;
    let rootOrganizations = selectors.rootOrganizations(state);

    if (rootOrganizations) {
      return rootOrganizations;
    }

    try {
      facets = await searchService.getFacets({
        context,
        query: {}
      });
    } catch (error) {
      if (error.status === 401) {
        throw error;
      }

      dispatch(handleFacetsError());

      return null;
    }

    const organizationIds = facets.organizations.map(({ id }) => id);
    const belongingOrganizationIds = getBelongingOrganizationIds(profile);

    rootOrganizations = organizationIds.filter((id) =>
      belongingOrganizationIds.includes(id)
    );

    return rootOrganizations;
  };
}

export function enableANGSearch() {
  return async (dispatch, getState) => {
    const state = getState();
    const isANGDefaultFlagEnabled = featureFlagsSelectors.featureFlag(
      state,
      ANG_DEFAULT_ENABLED
    );

    if (isANGDefaultFlagEnabled) {
      await dispatch(
        sessionActions.setUserProperty({
          namespace: 'exchange',
          key: 'isANGSearchDisabled',
          value: 'false'
        })
      );
    } else {
      await dispatch(
        sessionActions.setUserProperty({
          namespace: 'exchange',
          key: 'isANGSearchEnabled',
          value: 'true'
        })
      );
    }

    const homeURL = `${config.externalURI.pathname}/?new_search=`;

    dispatch(push(homeURL)); // hack trick to re-launch home resolver so facets are being loaded
  };
}

export function disableANGSearch() {
  return async (dispatch, getState) => {
    const state = getState();
    const isANGDefaultFlagEnabled = featureFlagsSelectors.featureFlag(
      state,
      ANG_DEFAULT_ENABLED
    );

    if (isANGDefaultFlagEnabled) {
      await dispatch(
        sessionActions.setUserProperty({
          namespace: 'exchange',
          key: 'isANGSearchDisabled',
          value: 'true'
        })
      );
    } else {
      await dispatch(
        sessionActions.setUserProperty({
          namespace: 'exchange',
          key: 'isANGSearchEnabled',
          value: 'false'
        })
      );
    }

    const homeURL = `${config.externalURI.pathname}/`;

    dispatch(push(homeURL));
  };
}

function replaceAssetTypes(state, assetTypesConfiguration, featureFlags) {
  const selectedTypes = state.filters[angConstants.TYPE_KEY];

  if (selectedTypes.length > 0) {
    return state;
  }

  const allANGTypes = Object.keys(assetTypesConfiguration).reduce(
    (accum, typeKey) => {
      const assetTypeConfiguration = assetTypesConfiguration[typeKey];

      if (!assetTypeConfiguration.angType) {
        return accum;
      }

      if (!assetTypeConfiguration.filter) {
        return accum;
      }

      if (
        assetTypeConfiguration.featureFlag &&
        !featureFlags[assetTypeConfiguration.featureFlag]
      ) {
        return accum;
      }

      if (
        accum.some(
          (existingType) => existingType === assetTypeConfiguration.angType
        )
      ) {
        return accum;
      }

      return [...accum, assetTypeConfiguration.angType];
    },
    []
  );

  return {
    ...state,
    filters: {
      ...state.filters,
      [angConstants.TYPE_KEY]: allANGTypes
    }
  };
}

export function search(filters, angConfig) {
  return (dispatch, getState) => {
    const state = getState();
    const assetTypesConfiguration = filters.types;
    const angState = filters.getAsANGState();
    const { authorization } = sessionSelectors.context(state);
    const featureFlags = featureFlagsSelectors.featureFlags(state);
    const queryController = ANGFactory.createQueryController({
      config: angConfig,
      authorization,
      state: replaceAssetTypes(angState, assetTypesConfiguration, featureFlags)
    });
    const queryExecutor = ANGFactory.createQueryExecutor();
    const isANGPaginationEnabled = selectors.isANGPaginationEnabled(state);
    const triggerSearchParams = isANGPaginationEnabled
      ? [angConstants.ANG_PAGINATION_SIZE]
      : [];

    let batchNumber = 0;
    let batchedAssets = [];
    const dispatchAssetBatch = () => {
      batch(() => {
        batchedAssets.forEach((batchedAsset) =>
          dispatch(addAsset(batchedAsset, batchNumber, isANGPaginationEnabled))
        );
        batchedAssets = [];
        batchNumber += 1;
      });
    };

    log('executing search', angState, angConfig);

    dispatch(triggerSearch(...triggerSearchParams));

    return new Promise((resolve, reject) => {
      queryExecutor.execute(queryController, {
        onFailure: ({ data, status }) => {
          const error = new Error(data);

          error.status = status;

          reject(error);
        },
        onFinish: (reason) => {
          const isSearchCancelled =
            reason?.code === angConstants.SEARCH_QUERY_CHANGED_CODE;

          if (!isSearchCancelled) {
            if (batchedAssets.length) {
              dispatchAssetBatch();
            }
            dispatch(finishSearch());
          }

          resolve();
        },
        onSearch: () => {},
        onStart: () => {},
        onHit: (hit) => {
          const type = getType(hit, assetTypesConfiguration);
          const ANGAssetAdapter =
            searchDefinitions[type]?.ANGAssetAdapter ?? GenericANGAssetAdapter;
          const ANGAssetHighlightAdapter =
            searchDefinitions[type]?.ANGAssetHighlightAdapter ??
            GenericANGAssetHighlightAdapter;
          const assetAdapter = new ANGAssetAdapter(
            hit,
            assetTypesConfiguration
          );
          const highlightAdapter = new ANGAssetHighlightAdapter(
            hit,
            assetTypesConfiguration
          );
          const asset = assetAdapter.parse();
          const highlight = highlightAdapter.parse();

          log('asset from ANG', hit);
          log('converted asset', asset);
          log('highlight', highlight);

          batchedAssets.push({ asset, highlight });

          if (
            batchedAssets.length >= angConstants.MAX_ANG_STREAM_BATCH_RESULTS
          ) {
            dispatchAssetBatch();
          }
        }
      });
    });
  };
}

export function optOutOfANGSearch() {
  return async (dispatch, getState) => {
    const state = getState();
    const isANGDefaultFlagEnabled = featureFlagsSelectors.featureFlag(
      state,
      ANG_DEFAULT_ENABLED
    );

    if (isANGDefaultFlagEnabled) {
      await dispatch(
        analyticsActions.trackEvent(EVENT_TYPES.ANG_SEARCH_OPT_OUT)
      );

      await dispatch(disableANGSearch());
    } else {
      dispatch(modalActions.openModal({ modalType: ANGOptOutModal.modalType }));
    }
  };
}

export function optInToANGSearch() {
  return async (dispatch, getState) => {
    const state = getState();
    const isANGDefaultFlagEnabled = featureFlagsSelectors.featureFlag(
      state,
      ANG_DEFAULT_ENABLED
    );

    if (isANGDefaultFlagEnabled) {
      await dispatch(
        analyticsActions.trackEvent(EVENT_TYPES.ANG_SEARCH_OPT_IN)
      );

      await dispatch(enableANGSearch());
    } else {
      dispatch(
        modalActions.openModal({ modalType: ANGOnboardingModal.modalType })
      );
    }
  };
}
